import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

export type ApiQueryError = FetchBaseQueryError | SerializedError | undefined;

export const getApiErrorStatus = (error: ApiQueryError): number | string | undefined => {
  if (!error || !('status' in error)) return undefined;
  return error.status;
};

export const isAuthApiError = (error: ApiQueryError): boolean =>
  getApiErrorStatus(error) === 401;

export const isNetworkApiError = (error: ApiQueryError): boolean => {
  const status = getApiErrorStatus(error);
  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
};

export const shouldShowDataLoadError = (
  isError: boolean,
  error?: ApiQueryError
): boolean => {
  if (!isError) return false;
  return !isAuthApiError(error);
};

export const shouldShowDataLoadErrorFromQueries = (
  ...queries: Array<{ isError?: boolean; error?: ApiQueryError }>
): boolean =>
  queries.some(query => shouldShowDataLoadError(!!query.isError, query.error));

export const refetchQueries = (
  ...refetchFns: Array<(() => unknown) | undefined>
): void => {
  refetchFns.forEach(refetch => refetch?.());
};

export const isAnyQueryFetching = (
  ...queries: Array<{ isFetching?: boolean }>
): boolean => queries.some(query => query.isFetching);

export type QueryErrorSource = {
  isError?: boolean;
  error?: ApiQueryError;
  refetch?: () => void;
  isFetching?: boolean;
};

export const getActiveQueryError = (sources: QueryErrorSource[]) => {
  const failedSources = sources.filter(source =>
    shouldShowDataLoadError(!!source.isError, source.error)
  );

  if (!failedSources.length) {
    return null;
  }

  return {
    error: failedSources[0].error,
    onRetry: () => {
      refetchQueries(
        ...sources
          .filter(source => source.isError)
          .map(source => source.refetch)
      );
    },
    isRetrying: isAnyQueryFetching(...sources),
  };
};
